import { z } from "zod";

const phoneNumberRegex = /^\+?[1-9]\d{1,14}$/;

/** Strict wire-format validation; unlike phoneNumberSchema, never strips input characters. */
export const e164PhoneNumberSchema = z.string().regex(/^\+[1-9]\d{1,14}$/);

export const phoneNumberSchema = z
  .string()
  .transform((value) => value.replace(/[^\d+]/g, ""))
  .refine((value) => phoneNumberRegex.test(value), {
    message: "Invalid phone number format",
  });

// Accepts exactly what formatPhoneNumber normalizes: formatPhoneNumber reads any leading `1` as
// the country code, so a bare 10-digit number cannot start with one.
export const northAmericanPhoneNumberSchema = phoneNumberSchema.refine(
  (value) => /^(?:\+?1\d{10}|[2-9]\d{9})$/.test(value),
  "Phone number must be a valid North American number"
);

/**
 * Normalizes a US phone number to North-American E.164 format (`+1XXXXXXXXXX`).
 * Strips spaces, dashes, parentheses, and an optional leading `1` or `+1`.
 * Throws if the input doesn't reduce to exactly 10 digits.
 */
export function formatPhoneNumber(phoneNumber: string): string {
  const validationResult = phoneNumberSchema.safeParse(phoneNumber);

  if (!validationResult.success) {
    throw new Error(validationResult.error.issues[0].message);
  }

  const cleanedNumber = phoneNumber.replace(/[^\d+]/g, "");
  const formattedNumber = cleanedNumber.startsWith("+1")
    ? cleanedNumber
    : `+1${cleanedNumber.startsWith("1") ? cleanedNumber.slice(1) : cleanedNumber}`;

  if (!/^\+1\d{10}$/.test(formattedNumber)) {
    throw new Error("Phone number must be in North American E.164 format (+1 followed by 10 digits)");
  }

  return formattedNumber;
}

/** Caller ID can be withheld or unavailable; only usable North American numbers survive. */
export function normalizeNorthAmericanPhoneNumber(value: string | null | undefined): string | null {
  const parsed = northAmericanPhoneNumberSchema.safeParse(value);

  return parsed.success ? formatPhoneNumber(parsed.data) : null;
}

/** Formats a US phone number for database lookup and display as `XXX-XXX-XXXX`. */
export function formatUSPhoneNumber(phoneNumber: string | null): string | null {
  if (!phoneNumber) return null;

  const digitsOnly = phoneNumber.replace(/\D/g, "");
  const nationalDigits = digitsOnly.length === 11 && digitsOnly.startsWith("1") ? digitsOnly.slice(1) : digitsOnly;

  if (nationalDigits.length === 10) {
    return `${nationalDigits.slice(0, 3)}-${nationalDigits.slice(3, 6)}-${nationalDigits.slice(6)}`;
  }

  const limitedDigits = digitsOnly.slice(0, 10);

  if (limitedDigits.length <= 3) return limitedDigits;
  if (limitedDigits.length <= 6) return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;

  return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
}

export function formatPhoneNumberForSpeech(phoneNumber: string): string {
  return formatUSPhoneNumber(formatPhoneNumber(phoneNumber))!
    .split("-")
    .map((group) => group.split("").join(" "))
    .join(", ");
}
